<script setup lang="ts">
import type { TextSettings, ThemeSettings } from '~/stores/auth'
import type { CommentItem as CommentItemType } from '~/stores/comments'

const props = defineProps<{
  comment: CommentItemType
  themeSettings: ThemeSettings
  textSettings: TextSettings
  isOwner: boolean
  isAuthor: boolean
  isLoggedIn: boolean
}>()

const emit = defineEmits<{
  delete: [commentId: string]
  report: [commentId: string]
}>()

const isDeleting = ref(false)
const isReporting = ref(false)

const isEdited = computed(() => {
  return props.comment.updated_at && props.comment.updated_at !== props.comment.created_at
    && new Date(props.comment.updated_at).getTime() > new Date(props.comment.created_at).getTime()
})

const relativeTime = computed(() => {
  const now = Date.now()
  const created = new Date(props.comment.created_at).getTime()
  const diff = now - created
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
})

const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))

const contentStyle = computed(() => ({
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 80%, transparent)`
}))

const metaStyle = computed(() => ({
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 40%, transparent)`
}))

function handleDelete() {
  isDeleting.value = true
  emit('delete', props.comment.id)
}

function handleReport() {
  isReporting.value = true
  emit('report', props.comment.id)
}
</script>

<template>
  <div class="flex gap-3 group">
    <!-- Author avatar -->
    <NuxtLink
      v-if="comment.author.username"
      :to="`/${comment.author.username}`"
      target="_blank"
      class="shrink-0"
    >
      <div class="size-8 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/10 hover:ring-white/25 transition-all duration-200">
        <img
          v-if="comment.author.avatar_url"
          :src="comment.author.avatar_url"
          :alt="comment.author.username"
          class="size-full object-cover"
        >
        <div
          v-else
          class="size-full flex items-center justify-center text-white/20"
        >
          <UIcon
            name="i-lucide-user"
            class="size-4"
          />
        </div>
      </div>
    </NuxtLink>
    <div
      v-else
      class="shrink-0"
    >
      <div class="size-8 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/10">
        <img
          v-if="comment.author.avatar_url"
          :src="comment.author.avatar_url"
          class="size-full object-cover"
        >
        <div
          v-else
          class="size-full flex items-center justify-center text-white/20"
        >
          <UIcon
            name="i-lucide-user"
            class="size-4"
          />
        </div>
      </div>
    </div>

    <!-- Comment content -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2">
        <NuxtLink
          v-if="comment.author.username"
          :to="`/${comment.author.username}`"
          target="_blank"
          class="text-xs font-medium hover:underline underline-offset-2 truncate"
          :style="{ color: `color-mix(in srgb, ${textSettings.color} 70%, transparent)` }"
        >
          {{ comment.author.display_name || comment.author.username }}
        </NuxtLink>
        <span
          v-else
          class="text-xs font-medium truncate"
          :style="{ color: `color-mix(in srgb, ${textSettings.color} 70%, transparent)` }"
        >
          {{ comment.author.display_name || 'Unknown' }}
        </span>
        <span
          class="text-[10px] shrink-0"
          :style="metaStyle"
        >
          {{ relativeTime }}
        </span>
        <span
          v-if="isEdited"
          class="text-[10px] shrink-0 italic"
          :style="metaStyle"
        >
          (edited)
        </span>
      </div>

      <p
        class="text-sm mt-0.5 break-words whitespace-pre-wrap"
        :style="contentStyle"
      >
        {{ comment.content }}
      </p>
    </div>

    <!-- Actions -->
    <div class="shrink-0 flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <UTooltip
        v-if="isLoggedIn && !isAuthor"
        text="Report"
      >
        <UButton
          icon="i-lucide-flag"
          color="neutral"
          variant="ghost"
          size="xs"
          :loading="isReporting"
          @click="handleReport"
        />
      </UTooltip>
      <UTooltip
        v-if="isAuthor || isOwner"
        text="Delete"
      >
        <UButton
          icon="i-lucide-trash-2"
          color="error"
          variant="ghost"
          size="xs"
          :loading="isDeleting"
          @click="handleDelete"
        />
      </UTooltip>
    </div>
  </div>
</template>
